// Pure matchers + types for the offline Bible-search stories bundle
// (Layer 4, Plan 05). This is the probe-importable leaf: its only runtime
// imports are the zero-import leaves `../search-normalize` and
// `../edit-distance`, so scripts/probe-bible-search-stories.mjs can load it
// under tsx without dragging in the browser-only bible/active facade. The
// fetch-based loader + version check live in the sibling `stories.ts` facade
// (which re-exports these), mirroring how `topic-search.ts` (pure) pairs
// with `topics.ts` (facade).
//
// The bundle is built offline by scripts/build-bible-search-stories.mjs from
// scripts/spine-catalog/ (rule #4 cross-boundary: the catalog is the source
// of truth, this static artifact is the offline duplicate). It carries
// verse REFS only (the pivotRef string); the pivot verse TEXT resolves from
// bundled BSB at render via the facade (rule #62/#76). Only auto_pair_safe
// rows are emitted (the safety gate is enforced at build, roadmap decision
// 3 + rule #76). en-US content only at v1 (rule #7).

import { normalizeForSearch } from '../search-normalize';
import { osaDistance, commonPrefixLength } from '../edit-distance';

export interface BundledStory {
  /** 'A' = named master-list story (has storyName + essence); 'B' = chapter fallback. */
  tier: 'A' | 'B';
  /** Catalog book slug (e.g. "1-samuel", "psalms"); identity + display only. */
  bookSlug: string;
  /** Tier A only; null for Tier B. Unique within a book. */
  storyKey: string | null;
  /** Tier B only (chapter number); null for Tier A. */
  chapter: number | null;
  /** Tier A name (e.g. "Samuel is Born"); null for Tier B (use processionName). */
  storyName: string | null;
  /** Tier A one-line essence; null for Tier B. */
  essence: string | null;
  /** Locked-vocab topic slugs this story is indexed under. */
  topics: string[];
  /** Pivot reference STRING (e.g. "1 Samuel 1:27"); verse text resolves at render. */
  pivotRef: string;
  /** The flowing prose narrative (the primary user-facing surface). */
  displayProse: string;
  /** Layer-1 procession name (e.g. "Hannah"). */
  processionName: string;
  /** Layer-1 role descriptor (e.g. "who wept for a child"). */
  roleDescriptor: string;
  /**
   * Curated do_not_pair_with tokens (lowercased at build; bundle v2).
   * Consumed by the tap-to-match SAVED-result safety gate
   * (src/lib/spine-clusters.ts suppression substrings, rule #76);
   * Bible-search rendering ignores it. Optional so probe fixtures and
   * any pre-v2 in-memory shapes stay valid.
   */
  doNotPairWith?: string[];
}

export interface StoriesBundle {
  stories: BundledStory[];
  /** Topic slug -> indexes into `stories` (build-time index, decision 1). */
  byTopic: Record<string, number[]>;
}

/**
 * Stable identity for dedup + React keys when the topic-driven and
 * direct-search paths surface the same story. Tier A is keyed by
 * book + storyKey; Tier B by book + chapter.
 */
export function storyId(s: BundledStory): string {
  return `${s.tier}:${s.bookSlug}:${s.storyKey ?? s.chapter}`;
}

/**
 * Stories curated under a topic, in bundle order (Tier A first). O(1) map
 * lookup over the build-time `byTopic` index; returns [] for an unknown
 * slug. Pure (no load); the caller passes the loaded bundle.
 */
export function storiesForTopic(slug: string, bundle: StoriesBundle): BundledStory[] {
  const idx = bundle.byTopic[slug];
  if (!idx) return [];
  const out: BundledStory[] = [];
  for (const i of idx) {
    const s = bundle.stories[i];
    if (s) out.push(s);
  }
  return out;
}

// Direct name/essence/procession matcher. Mirrors the verse-ranker fuzzy
// philosophy (bsb-search-core / rankPrayersFuzzy): exact AND-substring pass
// first (rule #38), and only on a zero-exact result a typo-tolerant fuzzy
// fallback where words < 4 chars are never fuzzed, the OSA threshold is 1
// for [4, 7) and 2 for >= 7, and a multi-word query needs >= 1 exact token
// anchor so a broad misspelling cannot all-fuzzy its way to junk.
const FUZZY_MIN_LEN = 4;
const FUZZY_LONG_LEN = 7;
const TOKEN_SPLIT_RE = /[^a-z0-9]+/;

// Common-name aliases for stories whose well-known name is NOT already in the
// curated fields (storyName / essence / procession / role). GAPS ONLY: names
// already matched by the curated content (noah, magi, pentecost, road to
// damascus, shadrach/meshach/abednego, david and goliath, eden, abraham and
// isaac, ...) are deliberately NOT listed. Keyed by storyId(s); folded into the
// haystack so searchStories surfaces the story by its common name. en-US only at
// v1 (rule #7), same posture as the catalog content. Punctuationless possessives
// included on purpose (`sauls conversion`) because normalizeForSearch does NOT
// strip apostrophes, so the curated "Saul's ..." would not exact-match an
// apostrophe-free query. Tier B common-name aliases (e.g. "woman at the well" ->
// B:john:4, "doubting thomas" -> B:john:20) are BANKED, not wired: Tier B cards
// render their heading from processionName (generic, rule #76 has no storyName),
// so a named-figure search landing on a generically-titled card is weak v1 UX.
export const STORY_ALIASES: Record<string, string[]> = {
  'A:luke:the-lost-son-luke-15-1-32': ['prodigal son', 'prodigal'],
  'A:genesis:the-great-flood-genesis-6-5-9-17': ['noahs ark', "noah's ark"],
  'A:genesis:adam-and-eve-genesis-2-8-3-24': ['garden of eden'],
  'A:genesis:abrahams-obedience-genesis-22-1-19': ['sacrifice of isaac', 'binding of isaac'],
  'A:jonah:jonah-jonah-1-1-4-11': ['jonah and the whale', 'whale', 'big fish'],
  'A:matthew:jesus-is-born': ['nativity', 'christmas story', 'birth of jesus'],
  'A:matthew:wise-men-and-king-herod': ['three kings', 'three wise men'],
  'A:matthew:jesus-feeds-5-000': ['feeding the 5000', 'feeding the five thousand'],
  'A:judges:samson-as-judge-judges-13-1-16-31': ['samson and delilah', 'delilah'],
  'A:acts:saul-encounters-jesus-acts-9-1-19': ['sauls conversion', 'pauls conversion', 'conversion of paul'],
  'A:matthew:the-triumphal-entry': ['palm sunday'],
};

function haystack(s: BundledStory): string {
  return normalizeForSearch(
    [s.storyName, s.essence, s.processionName, s.roleDescriptor, ...(STORY_ALIASES[storyId(s)] ?? [])]
      .filter((x): x is string => typeof x === 'string' && x !== '')
      .join(' '),
  );
}

// Strong-story-intent haystack: ONLY storyName + common-name aliases (NOT
// essence / procession / role). "The user typed THIS story's name" is a
// stronger signal than an incidental essence/procession word match, and it is
// the tier that leads the merged story lane in BibleBookIndex (08.3 piece 2,
// the gold-set-caught fix). Tier B has no storyName and no wired aliases, so
// its name-haystack is empty and it never enters the strong tier (rule #76).
function nameHaystack(s: BundledStory): string {
  return normalizeForSearch(
    [s.storyName, ...(STORY_ALIASES[storyId(s)] ?? [])]
      .filter((x): x is string => typeof x === 'string' && x !== '')
      .join(' '),
  );
}

/**
 * Stories whose NAME or common-name ALIAS WHOLE-TOKEN-AND matches the query —
 * the "strong story intent" tier (08.3 piece 2). Every query word must be a
 * whole token of the name haystack (NOT a substring), so `son` matches "The
 * Lost Son" but never "Joseph in Prison" or "Samson" — a substring match here
 * would manufacture false "explicit story intent" and bury topical stories.
 * NO fuzzy: an explicit name/alias is explicit intent and must not
 * fuzzy-expand. Tokenizes on whitespace (NOT TOKEN_SPLIT_RE) so apostrophe
 * tokens like `noah's` stay whole, matching the apostrophe-bearing aliases.
 * Empty query returns [] (matches searchStories, so an empty search can never
 * dump name hits). Preserves bundle order (Tier A first). The UI merges this
 * AHEAD of topic-driven + generic-direct so an explicit name match is never
 * buried past the lane cap, WITHOUT reordering broad topical story results (a
 * topical query has no name-token hit, so this returns [] for it).
 */
export function searchStoriesByName(query: string, stories: BundledStory[]): BundledStory[] {
  const nq = normalizeForSearch(query);
  if (!nq) return [];
  const words = nq.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return [];
  return stories.filter((s) => {
    const tokens = new Set(nameHaystack(s).split(' ').filter((t) => t.length > 0));
    if (tokens.size === 0) return false;
    return words.every((w) => tokens.has(w));
  });
}

/**
 * Stories matching a free-text query by name / essence / procession (NOT by
 * topic (that's storiesForTopic). Pure; the caller passes the loaded
 * stories array. Empty query returns [] (the section only renders on an
 * active query). Exact matches preserve bundle order (Tier A first); the
 * fuzzy fallback orders by total edit distance then bundle index.
 */
export function searchStories(query: string, stories: BundledStory[]): BundledStory[] {
  const nq = normalizeForSearch(query);
  if (!nq) return [];
  const words = nq.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return [];

  // Exact AND pass: every query word is a substring of the story's haystack.
  const exact = stories.filter((s) => {
    const hay = haystack(s);
    return words.every((w) => hay.includes(w));
  });
  if (exact.length > 0) return exact;

  // Fuzzy fallback (zero-exact only).
  const multiWord = words.length > 1;
  const scored: {
    story: BundledStory;
    index: number;
    dist: number;
    lenDelta: number;
    prefix: number;
  }[] = [];
  stories.forEach((s, index) => {
    const tokens = haystack(s).split(TOKEN_SPLIT_RE).filter(Boolean);
    const tokenSet = new Set(tokens);
    let totalDist = 0;
    // Match-quality signals to break distance ties (mirrors bsb-search-core):
    // a misspelling equidistant from the intended word AND several decoys
    // prefers the stronger shared prefix first, then the smaller length delta
    // (prefix before delta: same-length decoys would win on delta otherwise).
    // prefix is higher-is-better.
    let totalPrefix = 0;
    let totalLenDelta = 0;
    let anyExact = false;
    let ok = true;
    for (const w of words) {
      if (tokenSet.has(w)) {
        anyExact = true;
        totalPrefix += w.length; // exact: zero dist + delta, perfect prefix
        continue;
      }
      if (w.length < FUZZY_MIN_LEN) {
        ok = false;
        break;
      }
      const maxDist = w.length >= FUZZY_LONG_LEN ? 2 : 1;
      let bestDist = Infinity;
      let bestPrefix = -1;
      let bestLenDelta = Infinity;
      for (const tok of tokenSet) {
        if (Math.abs(tok.length - w.length) > maxDist) continue;
        const d = osaDistance(w, tok);
        if (d > maxDist) continue;
        const lenDelta = Math.abs(tok.length - w.length);
        const prefix = commonPrefixLength(w, tok);
        // Prefix before length delta (see bsb-search-core): the intended word
        // shares the longest leading run; same-length decoys would otherwise
        // win on delta.
        if (
          d < bestDist ||
          (d === bestDist && prefix > bestPrefix) ||
          (d === bestDist && prefix === bestPrefix && lenDelta < bestLenDelta)
        ) {
          bestDist = d;
          bestPrefix = prefix;
          bestLenDelta = lenDelta;
        }
      }
      if (bestDist === Infinity) {
        ok = false;
        break;
      }
      totalDist += bestDist;
      totalPrefix += bestPrefix;
      totalLenDelta += bestLenDelta;
    }
    if (!ok) return;
    if (multiWord && !anyExact) return;
    scored.push({ story: s, index, dist: totalDist, prefix: totalPrefix, lenDelta: totalLenDelta });
  });

  // Lowest distance first, then match quality (stronger shared prefix, then
  // smaller length delta), then bundle order as the stable tie-break.
  scored.sort((a, b) =>
    a.dist !== b.dist
      ? a.dist - b.dist
      : a.prefix !== b.prefix
        ? b.prefix - a.prefix
        : a.lenDelta !== b.lenDelta
          ? a.lenDelta - b.lenDelta
          : a.index - b.index,
  );
  return scored.map((x) => x.story);
}
